import { chakra, Flex, FormControl, FormLabel, useToast } from "@chakra-ui/react";
import { FormEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { CorporationIdentifiersDictionary, LockedAccount, ServiceSupplier, UserProfile } from "@bcasmart/doxecure-commons/doxecureTypes";
import { $count, $email, $ok } from "foundation-ts/commons";
import { TSCountry } from "foundation-ts/tscountry";
import { $bool } from "foundation-ts/tsparser";

import { G1Button } from "@components/G1Button";
import { G1Input } from "@components/G1Input";
import { ErrorField } from "@components/ErrorField";
import { SeaShellBox } from "@components/SeaShellBox";
import { CorporationForm } from "@components/CorporationForm/CorporationForm";
import { AdminOTPModal } from "@/components/Modals/AdminCorporateOTPModal";
import { config } from "@/config/config";
import useAvailableCorporationIdentifiers from "@hooks/useAvailableCorporationIdentifiers";
import { useAuthContext } from "@hooks/useAuthContext";
import useCountries from "@hooks/useCountries";
import usePatchUser from "@hooks/usePatchUser";
import { authenticateWithProvider, checkAdminOTP, deleteProviderChannel, sendAdminOTP } from "@services/authentication.service";
import { defaultErrorToast, defaultSuccessToast } from "@utils/toast";

interface AccountTabProps {
    lockedAccount?: LockedAccount;
    updateUserProfileView: () => void;
}

export const AccountTab = (props: AccountTabProps) => {
    const { lockedAccount, updateUserProfileView } = props;
    const { connectedUser, isAuthenticatedByOTP, setAuthenticatedByOTP } = useAuthContext();
    const { data: countries } = useCountries() ;
    const { data: availableCorporationIdentifiers } = useAvailableCorporationIdentifiers(countries);
    const [isCorporate, setIsCorporate] = useState(false) ;
    const [corporationIdentifiersDict, setCorporationIdentifiersDict] = useState<CorporationIdentifiersDictionary>({}) ;
    const [validINChannel, setValidINChannel] = useState(false) ;
    const [openOTPModal, setOpenOTPModal] = useState(false);
    const [disabledOrganizationName, setDisabledOrganizationName] = useState(true);
    const [disabledAddress, setDisabledAddress] = useState(true);
    const [disabledVATNumber, setDisabledVATNumber] = useState(true);
    const [disabledAdminButton, setDisabledAdminButton] = useState(false);
    const { mutateAsync: patchUser } = usePatchUser() ;
    const {
        getValues,
        register,
        setValue,
        formState: { errors },
    } = useForm<any>({
        mode: "onChange",
        reValidateMode: "onChange",
    });
    const navigate = useNavigate();
    const toast = useToast() ;

    const handleSendOTP = async (): Promise<boolean> => {
        return await sendAdminOTP(connectedUser?.apid);
    }

    const handleAuthenticateWithOTP = async (otp: string): Promise<boolean> => {
        let isValid = false;
        try {
            isValid = await checkAdminOTP(connectedUser?.apid, otp);
            if (isValid) {
                setDisabledOrganizationName(false);
                setDisabledAddress(false);
                setDisabledVATNumber(false);
                setDisabledAdminButton(true);
                updateUserProfileView();
                setAuthenticatedByOTP(true);
            }
        } catch {}
        return isValid;
    }

    const handleOpenModalOTP = async (): Promise<void> => {
        setOpenOTPModal(true);
        await handleSendOTP();
    }

    const handleChangeProfile = async (e: FormEvent<HTMLFormElement>) => { //SubmitHandler<any> = async (data: UserDto) => {
        try {
            e.preventDefault() ;
            const email = $email(getValues<string>("email")) ;
            await patchUser({
                apid: connectedUser?.apid,
                firstName: getValues<string>("firstName"),
                lastName: getValues<string>("lastName"),
                email: $ok(email) ? email! : connectedUser!.email
            }) ;
            const message = (email?.toLowerCase() !== connectedUser?.email) ? `Un mail de validation de changement d'email vous a été envoyé sur l'adresse ${email}. Veuillez terminer la procédure pour que le changement soit pris en compte.` : "Profil mis à jour" ;
            toast(defaultSuccessToast(message)) ;
        } catch (error) {
            toast(defaultErrorToast("Problème rencontré lors de la mise à jour")) ;
        }
    } ;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValue(name, value) ;
    }

    const handleDeleteProviderChannel = async () => {
        if (await deleteProviderChannel(connectedUser?.apid!, ServiceSupplier.Chambersign)) {
            toast(defaultSuccessToast("Votre identité numérique a été supprimé de votre compte. Vous ne pourrez donc plus vous authentifier avec elle.")) ;
            setValidINChannel(false) ;
        } else {
            toast(defaultErrorToast("Impossible de supprimer votre identité numérique de votre compte.")) ;
        }
    }

    useEffect(() => { 
        const belongsToCorporate = connectedUser?.userFlags?.profile === UserProfile.UserCorporate || connectedUser?.userFlags?.profile === UserProfile.AdministratorCorporate ;
        setIsCorporate(belongsToCorporate) ;
        if (belongsToCorporate) {
            const corporation = connectedUser.corporations?.first() ;
            setValue("organizationName", corporation?.name || "") ;
            setValue("organizationIdentifier", corporation?.corporationIdentifier || "") ;
            setValue("organizationIdentifierType", corporation?.corporationIdentifierType);
            setValue("vatNumber", corporation?.vatNumber || "") ;
            setValue("streetNumber", corporation?.address?.streetNumber || "") ;
            setValue("street", corporation?.address?.street || "") ;
            setValue("streetComplement", corporation?.address?.complement || "") ;
            setValue("zipCode", corporation?.address?.zipCode || "") ;
            setValue("city", corporation?.address?.city || "") ;
            setValue("organizationCountry", corporation?.address?.country || countries?.first()?.code) ;
            const newCountry = TSCountry.country(corporation?.address?.country || countries?.first()?.code)?.alpha2Code ;
            if ($ok(newCountry)) {
                const newDict = availableCorporationIdentifiers?.get(newCountry!) ;
                if ($ok(newDict)) { 
                    setCorporationIdentifiersDict(newDict!) ;
                }
            }
        } else {
            setValue("organizationName", connectedUser?.organizationName || "") ;
            setValue("vatNumber", connectedUser?.vatNumber || "") ;
            setValue("company_streetNumber", connectedUser?.organizationAddress?.streetNumber || "") ;
            setValue("company_street", connectedUser?.organizationAddress?.street || "") ;
            setValue("company_streetComplement", connectedUser?.organizationAddress?.complement || "") ;
            setValue("company_zipCode", connectedUser?.organizationAddress?.zipCode || "") ;
            setValue("company_city", connectedUser?.organizationAddress?.city || "") ;
            setValue("company_country", connectedUser?.organizationAddress?.country || countries?.first()?.code) ;
        }
        setValue("lastName", connectedUser?.lastName || "") ;
        setValue("firstName", connectedUser?.firstName || "") ;
        setValue("email", connectedUser?.email.toString() || "") ;
        setValidINChannel($bool(connectedUser?.userFlags?.hasValidINChannel)) ;

        setDisabledAdminButton(isAuthenticatedByOTP);
        if (connectedUser?.userFlags?.profile === UserProfile.AdministratorCorporate && isAuthenticatedByOTP) {
            setDisabledAddress(false);
            setDisabledOrganizationName(false);
            setDisabledVATNumber(false);
            updateUserProfileView();
        }
    }, [connectedUser, availableCorporationIdentifiers]) ;

    return (
        <chakra.form noValidate onSubmit={handleChangeProfile}>
            <SeaShellBox component="form">
                <FormControl isRequired>
                    <FormLabel fontSize="1.25em">Nom</FormLabel>
                    <G1Input component="form" registerInput={{ ...register("lastName", { required: true }) }} />
                    {errors.lastName && <ErrorField fieldName="nom" />}
                </FormControl>

                <FormControl isRequired>
                    <FormLabel fontSize="1.25em">Prénom</FormLabel>
                    <G1Input component="form" registerInput={{ ...register("firstName", { required: true }) }} />
                    {errors.firstName && <ErrorField fieldName="prénom" />}
                </FormControl>

                <FormControl isRequired>
                    <FormLabel fontSize="1.25em">Adresse email</FormLabel>
                    <G1Input component="form" type="email" registerInput={{ ...register("email", { required: true }) }} disabled={$count(connectedUser?.corporations) > 0 || connectedUser?.userFlags?.hasValidINChannel} />
                    {errors.email && <ErrorField fieldName="email" />}
                </FormControl>

                {isCorporate ? 
                    <CorporationForm required disabledOrganizationName={disabledOrganizationName} disabledVATNumber={disabledVATNumber} disabledAddress={disabledAddress} showUniqueId label="Organisation" countries={countries} corporationIdentifiersDict={corporationIdentifiersDict} errors={errors} register={register} onChange={handleChange} />
                    : <CorporationForm label="Nom de l'entreprise" required disabledOrganizationName disabledVATNumber disabledAddress prepId="company_" countries={countries} errors={errors} register={register} onChange={handleChange} />
                }
            </SeaShellBox>
            <Flex direction={{ base: "column", "2xl": "row" }} gap={4} justifyContent="space-evenly" width="100%" style={{ marginTop: "10px" }}>
                <G1Button component="form" variant="important" title="Changer de mot de passe" whiteSpace="normal" fontSize="lg"
                          flex={{ "2xl": 1 }} width={{ base: "100%" }} height={{ base: "60px", xl: "auto" }} minHeight="60px" rounded="xl"
                          disabled={lockedAccount?.exceedsUsersLimit || lockedAccount?.canceledAndWaitingSessionEnds} onClick={() => navigate("/app/recover")}
                />
                <G1Button component="form" variant="normal" title="Sauvegarder ma fiche" whiteSpace="normal" type="submit" fontSize="lg"
                          flex={{ "2xl": 1 }} width={{ base: "100%" }} height={{ base: "60px", xl: "auto" }} minHeight="60px" rounded="xl"
                          disabled={lockedAccount?.exceedsUsersLimit || lockedAccount?.canceledAndWaitingSessionEnds}
                />
                {$ok(config.providers["chambersign"].clientId) && !validINChannel &&
                    <G1Button component="form" variant="optional" title="Associer mon identité numérique" whiteSpace="normal" fontSize="lg"
                              flex={{ "2xl": 1 }} width={{ base: "100%" }} height={{ base: "60px", xl: "auto" }} minHeight="60px" rounded="xl"
                              disabled={lockedAccount?.exceedsUsersLimit || lockedAccount?.canceledAndWaitingSessionEnds} onClick={() => authenticateWithProvider(ServiceSupplier.Chambersign, connectedUser?.apid)}
                    />
                }
                {$ok(config.providers["chambersign"].clientId) && validINChannel &&
                    <G1Button component="form" variant="optional" title="Supprimer mon identité numérique" whiteSpace="normal" fontSize="lg"
                        flex={{ "2xl": 1 }} width={{ base: "100%" }} height={{ base: "60px", xl: "auto" }} minHeight="60px" rounded="xl"
                        disabled={lockedAccount?.exceedsUsersLimit || lockedAccount?.canceledAndWaitingSessionEnds} onClick={handleDeleteProviderChannel}
                    />
                }
                {connectedUser?.userFlags?.profile === UserProfile.AdministratorCorporate && 
                    <G1Button component="form" variant="alternate" title="Gérer l'organisation" whiteSpace="normal" fontSize="lg"
                        flex={{ "2xl": 1 }} width={{ base: "100%" }} height={{ base: "60px", xl: "auto" }} minHeight="60px" rounded="xl"
                         disabled={disabledAdminButton} onClick={handleOpenModalOTP}
                    />
                }
            </Flex>
            <AdminOTPModal isOpen={openOTPModal} userEmail={connectedUser?.email}
                onSendOTP={handleSendOTP}
                onAuthenticateWithOTP={handleAuthenticateWithOTP}
                onClose={async function (): Promise<void> {
                    setOpenOTPModal(false);
                }}
            />
        </chakra.form>
    );
} ;